import React from "react";
import { Bell, Zap, Clock, Pause } from "lucide-react";
import type { PriceAlertStatsData } from "@/api/price-alerts";

interface PriceAlertStatsCardsProps {
  stats: PriceAlertStatsData | undefined;
  isLoading: boolean;
}

interface StatCard {
  label: string;
  value: number;
  icon: React.ReactNode;
  iconBgClass: string;
  valueClass?: string;
  subLabel?: string;
  subLabelClass?: string;
}

export const PriceAlertStatsCards: React.FC<PriceAlertStatsCardsProps> = ({
  stats,
  isLoading,
}) => {
  if (isLoading || !stats) {
    const skeletonKeys = ["sk-1", "sk-2", "sk-3", "sk-4"];
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {skeletonKeys.map((key) => (
          <div
            key={key}
            className="bg-tertiary border border-border rounded-xl p-4 md:p-6 animate-pulse"
          >
            <div className="w-10 h-10 rounded-full bg-secondary mb-3" />
            <div className="h-3 w-20 bg-secondary rounded mb-2" />
            <div className="h-6 w-12 bg-secondary rounded" />
          </div>
        ))}
      </div>
    );
  }

  const cards: StatCard[] = [
    {
      label: "Active alerts",
      value: stats.activeAlerts,
      icon: <Bell className="w-5 h-5 text-green-500" />,
      iconBgClass: "bg-green-500/10",
    },
    {
      label: "Triggered (7d)",
      value: stats.triggered7d,
      icon: <Zap className="w-5 h-5 text-text-secondary" />,
      iconBgClass: "bg-secondary",
      subLabel:
        stats.triggeredToday > 0
          ? `\u2191 ${stats.triggeredToday} today`
          : undefined,
      subLabelClass: "text-green-500",
    },
    {
      label: "Exchanges watched",
      value: stats.exchangesWatched,
      icon: <Clock className="w-5 h-5 text-text-secondary" />,
      iconBgClass: "bg-secondary",
    },
    {
      label: "Paused",
      value: stats.paused,
      icon: <Pause className="w-5 h-5 text-red-500" />,
      iconBgClass: "bg-red-500/10",
      valueClass: "text-red-500",
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {cards.map((card) => (
        <div
          key={card.label}
          className="bg-tertiary border border-border rounded-xl p-4 md:p-6"
        >
          <div className="mb-3">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center ${card.iconBgClass}`}
            >
              {card.icon}
            </div>
          </div>
          <p className="text-sm text-text-secondary mb-1">{card.label}</p>
          <div className="flex items-baseline gap-2">
            <p
              className={`text-2xl font-bold ${card.valueClass || "text-text-primary"}`}
            >
              {card.value}
            </p>
            {card.subLabel && (
              <span className={`text-xs font-medium ${card.subLabelClass || "text-text-secondary"}`}>
                {card.subLabel}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
